import { isOwner, isIssueOpen, FullProjectIssue, actions } from 'entities';

import { Comments } from 'components/Comments';

import { useUser } from 'hooks/user';
import { useProjectProfile } from 'pages/Projects/ProjectProfile/context';

import { listPartialUpdate } from 'utils/array';

import { BaseProps } from './types';

export function IssueComments({ issue, updateIssue }: Omit<BaseProps, 'close' | 'setTab'>) {
  const user = useUser();

  const { project } = useProjectProfile();

  const create = actions.projects.createIssueComment.use({
    onSuccess: (comment) => {
      updateIssue({
        ...(issue as FullProjectIssue),

        comments: [comment, ...(issue?.comments || [])],
      });
    },
  });

  const createComment = async (newComment: string, files: File[]): Promise<boolean | void> => {
    if (!newComment) return;

    const comment = await create.execute({
      issue: issue!.id,
      project: issue!.project,
      text: newComment,
      files,
    });

    return !!comment;
  };

  const edit = actions.projects.editIssueComment.use({
    onSuccess: (_, { id, text }) => {
      updateIssue({
        ...(issue as FullProjectIssue),

        comments: listPartialUpdate(issue!.comments, { edited: true, text, id }),
      });
    },
  });

  const editComment = async (
    text: string,
    editedComment: FullProjectIssue['comments'][number],
  ): Promise<void> => {
    if (!text) return;

    await edit.execute({
      id: editedComment.id,
      project: editedComment.project,
      issue: editedComment.issue,
      text,
    });
  };

  const canComment =
    isOwner(project, user) ||
    [
      issue?.owner,
      ...(issue?.salesInvolved || []).map((sales) => sales.user),
      ...(issue?.executorsInvolved || []).map((executor) => executor.user),
    ].some((involved) => involved?.id === user.id);

  return (
    <Comments
      canEdit={canComment && isIssueOpen(issue)}
      comments={issue?.comments || []}
      onCreate={createComment}
      onEdit={editComment}
      inputBellowTitle
      historyTarget="projects.issueCommentHistory"
    />
  );
}
